import {
  Checkbox,
  FormGroup,
  FormQuerySelector,
  QuerySelector,
  RadioGroup,
  Toast,
} from "@vesatogo/grass-core"
import dayjs from "dayjs"
import { Link, useSearchParams } from "react-router-dom"
import { FormCard } from "~/components/FormCard"
import { TradeKinds } from "~/constants/Kinds"
import SharedEntityGroup from "~/constants/SharedEntityGroup"
import {
  useAllCommoditiesQuery,
  useAllPlotCropsQuery,
  useAllVarietiesQuery,
  useStorageLocationSelectorQuery,
  useStorageSubLocationSelectorQuery,
} from "~/generated/graphql"

type WhatIsBeingTradedProps = {
  form: any
  inputProps: any
  isDisabled?: boolean
  disableCommodity?: boolean
  disablePlotCrop?: boolean
  disableVariety?: boolean
  isMulti?: boolean
}
const WhatIsBeingTraded = ({
  form,
  inputProps,
  isDisabled,
  disableCommodity,
  disablePlotCrop,
  disableVariety,
  isMulti = false,
}: WhatIsBeingTradedProps) => {
  const [searchParams] = useSearchParams()
  const isNewStorage = searchParams.get("newStorage") === "true"
  const { values: state } = form
  const isStorage = inputProps("kind").value === TradeKinds.StoragePatti
  const isInward = inputProps("kind").value === TradeKinds.InwardPatti
  const isPackaging = inputProps("material_kind").value === "packaging"
  const canViewStorageLocation = isStorage

  return (
    <FormCard title="What is being traded">
      <div className="md:grid md:grid-cols-4 md:flex-row gap-4">
        {isPackaging || isMulti ? null : (
          <>
            {" "}
            <FormQuerySelector
              label="Commodity"
              serverSideQuery
              dataHook={useAllCommoditiesQuery}
              {...inputProps("commodity", {
                group: SharedEntityGroup.KiteCommodity,
              })}
              className={"w-full"}
              required
              isDisabled={isDisabled || disableCommodity}
            />
            <FormQuerySelector
              label="Variety"
              dataHook={useAllVarietiesQuery}
              variables={{ commodity_id: form.values.commodity?.id }}
              {...inputProps("commodity_variety", {
                group: SharedEntityGroup.KiteVariety,
              })}
              className={"w-full col-span-2"}
              required
              isDisabled={isDisabled || disableVariety}
            />
            <FormGroup label="Plot Selection" className={"w-full"}>
              <QuerySelector
                dataHook={useAllPlotCropsQuery}
                variables={{
                  farmer: form.values.seller?.id,
                  crop: form.values.commodity?.crop_id,
                }}
                {...inputProps("plot_crop", {
                  group: SharedEntityGroup.KitePlotCrop,
                })}
                getOptionLabel={({ tag, name, external_reference_id, id }) => {
                  if (!id) return undefined
                  return `${tag} ${name} | ${external_reference_id}`
                }}
                isDisabled={isDisabled || disablePlotCrop}
              />
            </FormGroup>{" "}
          </>
        )}
        {canViewStorageLocation && (
          <>
            <FormQuerySelector
              label={isInward ? "Inward Location" : "Storage Location"}
              dataHook={useStorageLocationSelectorQuery}
              {...inputProps("storage_location")}
              className={"w-full"}
              isDisabled={isNewStorage ? false : isDisabled}
              serverSideQuery
              useWildCardsInQuery
            />
            <FormQuerySelector
              label={isInward ? "Inward Sub Location" : "Storage Sub Location"}
              dataHook={useStorageSubLocationSelectorQuery}
              variables={{
                where: {
                  storage_location_id: {
                    _eq: form.values.storage_location?.id,
                  },
                },
              }}
              {...inputProps("storage_sub_location")}
              className={"w-full"}
              isDisabled={isNewStorage ? false : isDisabled}
            />
          </>
        )}
      </div>
      {(() => {
        const storageLocation = state?.storage_location
        if (!storageLocation) return null

        const agreement = storageLocation?.storage_location_agreements?.[0]
        if (!agreement) {
          return (
            <Toast
              className="!text-xs !min-w-full mt-2"
              variant="minimal"
              intent="danger"
              titleClassName="mt-1"
              title={
                <Link
                  to={`/d/storage-locations/${storageLocation.id}`}
                  className="flex justify-between items-center w-full"
                  target="_blank"
                >
                  <span>
                    {`Your storage location agreement has expired. Please renew it
                    to continue using the service. Click here>>`}
                  </span>
                </Link>
              }
            />
          )
        }

        const expiryDate = dayjs(agreement.end_date)
        const today = dayjs()
        const daysToExpiry = expiryDate.diff(today, "day")
        const isExpired =
          expiryDate.isBefore(today, "day") ||
          (expiryDate.isSame(today, "day") && expiryDate.isBefore(today))

        if (isExpired || (daysToExpiry <= 7 && daysToExpiry >= 0)) {
          const message = isExpired
            ? "Your storage location agreement has expired. Please renew it to continue using the service. Click here>>"
            : `Your storage location agreement is expiring in ${daysToExpiry} day${
                daysToExpiry === 1 ? ". " : "s. "
              }Please renew it to continue using the service. Click here>>`
          return (
            <Toast
              className="!text-xs !min-w-full mt-2"
              variant="minimal"
              intent={isExpired ? "danger" : "warning"}
              titleClassName="mt-1 w-full"
              title={
                <Link
                  to={`/d/storage-locations/${storageLocation.id}`}
                  className="flex justify-between items-center w-full"
                  target="_blank"
                >
                  <span>{message}</span>
                </Link>
              }
            />
          )
        }

        return null
      })()}
      {isPackaging ? null : (
        <div className="mt-2">
          <RadioGroup
            label="Packaging type"
            required
            {...inputProps("packaging")}
          >
            <Checkbox
              type={"radio"}
              value="raw_material"
              label="Raw Material"
              disabled={isDisabled}
            />
            <Checkbox
              type={"radio"}
              value="finished_goods"
              label="Finished Goods"
              disabled={isDisabled}
            />
          </RadioGroup>
        </div>
      )}
    </FormCard>
  )
}

export default WhatIsBeingTraded
